import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useAuth } from '@/features/auth/context/authProvider'
import { FormFieldConfig } from '@/shared/components/DynamicForm/DynamicForm'
import IconButton from '@/shared/components/IconButton/IconButton'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import AccountPhotoField from '../components/AccountPhotoField'
import PasswordLinkField from '../components/PasswordLinkField'
import SignOutEverywhereField from '../components/SignOutEverywhereField'

/**
 * Settings > Account as a DynamicForm: the profile (the photo, with the name,
 * the email and the account id beside it, as the venue's logo has its name),
 * then the account's two actions as text buttons. No headings and no hints:
 * each label and button says what it is, and what happened is told in the
 * banners. Only the name is saved by the form. The venues are in
 * Settings > Restaurant, where they are switched between.
 */
export const useAccountFormConfig = () => {
  const { t } = useTranslation()
  const { currentUser } = useAuth()
  const { showSnackbar } = useSnackbar()

  const copyAccountId = () =>
    navigator.clipboard.writeText(currentUser?.uid ?? '').then(
      () =>
        showSnackbar(
          t('ALERTS.ACCOUNT_ID_COPIED.TITLE'),
          t('ALERTS.ACCOUNT_ID_COPIED.DESCRIPTION'),
          'success'
        ),
      () =>
        showSnackbar(
          t('ALERTS.ACCOUNT_ID_COPY_ERROR.TITLE'),
          t('ALERTS.ACCOUNT_ID_COPY_ERROR.DESCRIPTION'),
          'error'
        )
    )

  const fields: FormFieldConfig[] = [
    {
      name: '',
      type: 'input-group',
      alignItems: 'flex-start',
      inputs: [
        {
          name: 'photo',
          type: 'custom',
          flexGrow: 0,
          render: () => <AccountPhotoField />
        },
        {
          name: '',
          type: 'input-group',
          inputGroupDirection: 'column',
          inputs: [
            {
              name: 'displayName',
              type: 'text',
              label: t('SETTINGS.ACCOUNT.NAME'),
              placeholder: t('SETTINGS.ACCOUNT.NAME_PLACEHOLDER'),
              focused: true,
              required: true
            },
            {
              name: 'email',
              type: 'read-only',
              label: t('SETTINGS.ACCOUNT.EMAIL')
            },
            {
              // For a message to support, as Claude's organisation id is.
              name: 'accountId',
              type: 'read-only',
              label: t('SETTINGS.ACCOUNT.ID'),
              endAdornment: (
                <IconButton
                  icon="las la-copy icon-medium"
                  color="primary"
                  ariaLabel={t('SETTINGS.ACCOUNT.ID_COPY')}
                  onClick={copyAccountId}
                />
              )
            }
          ]
        }
      ]
    },
    {
      name: '',
      type: 'input-group',
      inputs: [
        {
          name: 'password',
          type: 'custom',
          flexGrow: 0,
          render: () => <PasswordLinkField />
        },
        {
          name: 'signOutEverywhere',
          type: 'custom',
          flexGrow: 0,
          render: () => <SignOutEverywhereField />
        }
      ]
    }
  ]

  // Only the name: everything else is shown, and never sent.
  const validationSchema = z.object({
    displayName: z
      .string()
      .trim()
      .min(1, t('SETTINGS.ACCOUNT.NAME_REQUIRED'))
  })

  return { fields, validationSchema }
}

export default useAccountFormConfig
